import { supabase } from './supabaseClient';

export const tournamentService = {
  /**
   * Save parsed tournament to Supabase
   * Converts your flat JSON structure to relational database
   */
  async saveTournament(parsedData) {
    try {
      console.log('Saving tournament:', parsedData.title);

      // 1. Insert packet (main tournament info)
      const { data: packet, error: packetError } = await supabase
        .from('packets')
        .insert([{
          title: parsedData.title,
          difficulty: parsedData.difficulty,
          date: parsedData.date,
          questions_count: parsedData.questions,
          published: true
        }])
        .select()
        .single();

      if (packetError) throw packetError;

      const packetId = packet.id;
      console.log('✓ Packet saved with ID:', packetId);

      // 2. Insert authors
      if (parsedData.author && parsedData.author.length > 0) {
        const authorsToInsert = parsedData.author.map(authorString => {
          // Parse "ირაკლი გუნია (16)" format
          const match = authorString.match(/^(.+?)\s*\((\d+)\)$/);
          if (match) {
            return {
              packet_id: packetId,
              author_name: match[1].trim(),
              question_count: parseInt(match[2])
            };
          } else {
            return {
              packet_id: packetId,
              author_name: authorString.trim(),
              question_count: null
            };
          }
        });

        const { error: authorsError } = await supabase
          .from('packet_authors')
          .insert(authorsToInsert);

        if (authorsError) throw authorsError;
        console.log('✓ Authors saved:', authorsToInsert.length);
      }

      // 3. Insert questions
      if (parsedData.questions_data && parsedData.questions_data.length > 0) {
        const questionsToInsert = parsedData.questions_data.map((q, index) => ({
          packet_id: packetId,
          question_number: index + 1,
          text: q.text,
          answer: q.answer,
          author: q.author || '',
          comment: q.comment || '',
          source: q.source || '',
          url: q.url || '',
          image: q.image || null
        }));

        const { error: questionsError } = await supabase
          .from('questions')
          .insert(questionsToInsert);

        if (questionsError) throw questionsError;
        console.log('✓ Questions saved:', questionsToInsert.length);
      }

      console.log('✓ Tournament saved successfully!');
      return packet;

    } catch (error) {
      console.error('Error saving tournament:', error);
      throw error;
    }
  },

  /**
   * Get all tournaments for admin view
   */
  async getAllTournaments() {
    const { data, error } = await supabase
      .from('packets')
      .select(`
        *,
        packet_authors (
          author_name,
          question_count
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  /**
   * Get single tournament with all questions
   */
  async getTournament(id) {
    const { data: packet, error: packetError } = await supabase
      .from('packets')
      .select(`
        *,
        packet_authors (
          author_name,
          question_count
        ),
        questions (
          *
        )
      `)
      .eq('id', id)
      .single();

    if (packetError) throw packetError;
    return packet;
  },

  /**
   * Delete tournament
   */
  async deleteTournament(id) {
    const { error } = await supabase
      .from('packets')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Export all tournaments to JSON format (for static serving)
   * This converts relational DB back to your flat JSON structure
   */
  async exportToJSON() {
    try {
      // Get all published packets with authors and questions
      const { data: packets, error } = await supabase
        .from('packets')
        .select(`
          *,
          packet_authors (
            author_name,
            question_count
          ),
          questions (
            *
          )
        `)
        .eq('published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Convert to your JSON format
      const exportData = {
        packets: packets.map(packet => ({
          id: packet.id,
          title: packet.title,
          difficulty: packet.difficulty,
          questions: packet.questions_count,
          author: packet.packet_authors.map(a => 
            a.question_count 
              ? `${a.author_name} (${a.question_count})`
              : a.author_name
          ),
          date: packet.date,
          questions_data: packet.questions
            .sort((a, b) => a.question_number - b.question_number)
            .map(q => ({
              id: q.question_number,
              text: q.text,
              answer: q.answer,
              author: q.author,
              comment: q.comment,
              source: q.source,
              url: q.url,
              image: q.image
            }))
        }))
      };

      return exportData;

    } catch (error) {
      console.error('Error exporting to JSON:', error);
      throw error;
    }
  }
};
